import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { ResolverTag } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import type { Subscription } from 'rxjs';
import { merge as mergeOperator } from 'rxjs';

import { type WidgetEffectParams } from '@swf/core/store';
import type { WidgetReference } from '@swf/core/types/Widget';
import { authChange, currencyChange } from '@swf/core/utils/actions';
import { fetchJson } from '@swf/core/utils/api';

import { CLIENT_HSP_PLATFORM_MAP } from '../../constants/';
import type {
  FiltersConfig,
  TournamentQueryResponse,
  TournamentsUpdateResponse
} from '../../types/MTT';
import {
  getMTTDataTypeKey,
  getMTTSubscriptionDataKey,
  normalizeTournamentsData
} from '../../utils/MTT';
import { normalizeFiltersData } from '../../utils/MTT/filters';

import { DOCUMENT_DATA_TYPE_MAP, GRAPHQL_API_URL } from './constants';
import type { WidgetDispatch, WidgetSelect } from './types';

let tournamentSubscription: Subscription;

export async function preload<T extends WidgetReference>(
  _payload: undefined,
  { actions, dispatch, widget, select, rootState }: WidgetEffectParams<T>
) {
  const {
    clear,
    tournamentsRequested,
    filtersDataRequested,
    subscribeToTournaments
  }: WidgetDispatch = dispatch[widget.uid];
  const { getFiltersData }: WidgetSelect = select[widget.uid];
  const authChangeObservable = authChange(actions);
  const currencyChangeObservable = currencyChange(actions);

  if (!canUseDOM) {
    await Promise.all([tournamentsRequested(), filtersDataRequested()]);
  } else {
    subscribeToTournaments({});
    if (!getFiltersData(rootState).length) filtersDataRequested();
  }

  if (currencyChangeObservable && authChangeObservable) {
    mergeOperator(currencyChangeObservable, authChangeObservable).subscribe((subscribeData) =>
      subscribeToTournaments({
        setLoading: subscribeData.type === 'currency/set'
      })
    );
  }
  widget.unmount.subscribe(clear);
}

export async function tournamentsRequested<T extends WidgetReference>(
  _payload: undefined,
  { dispatch, select, widget, rootState, request }: WidgetEffectParams<T>
): Promise<void> {
  const { tournamentsLoadSuccess, tournamentsLoadFailure }: WidgetDispatch = dispatch[widget.uid];
  const { getConfigQueryVariables }: WidgetSelect = select[widget.uid];

  const locale = select.locale.get(rootState);
  const timeZoneName = select.timeZone.getTimeZoneName(rootState);

  const { mttDataType = '' } = select.config.get(rootState);
  const DATA_KEY = getMTTDataTypeKey(mttDataType);
  const variables = getConfigQueryVariables(rootState, { widget });
  const requestBody = { query: DOCUMENT_DATA_TYPE_MAP[DATA_KEY].QUERY, variables };

  try {
    const {
      body: { data, errors }
    } = await request<TournamentQueryResponse>({
      service: GRAPHQL_API_URL.HTTP,
      http: true,
      method: 'POST',
      body: requestBody
    });

    if (data)
      tournamentsLoadSuccess(normalizeTournamentsData(data[DATA_KEY], locale, timeZoneName));
    if (errors)
      tournamentsLoadFailure(errors.reduce((acc, { message }) => (acc += ` ${message}`), ''));
  } catch (err) {
    tournamentsLoadFailure((err as Error)?.toString?.());
  }
}

export async function subscribeToTournaments<T extends WidgetReference>(
  { setLoading = false }: { setLoading?: boolean },
  { dispatch, select, widget, rootState, requestStream }: WidgetEffectParams<T>
): Promise<void> {
  const { updateTournament, setSubscriptionError, setTournamentsLoading }: WidgetDispatch =
    dispatch[widget.uid];
  const { getConfigQueryVariables }: WidgetSelect = select[widget.uid];

  const { mttDataType = '' } = select.config.get(rootState);
  const dataKey = getMTTDataTypeKey(mttDataType);
  const locale = select.locale.get(rootState);
  const options = select.cms.getRequestOptions(rootState);
  const subscriptionDataKey = getMTTSubscriptionDataKey(mttDataType);
  const timeZoneName = select.timeZone.getTimeZoneName(rootState);
  const variables = getConfigQueryVariables(rootState, { widget });

  if (setLoading) setTournamentsLoading();
  if (tournamentSubscription && !tournamentSubscription.closed)
    tournamentSubscription.unsubscribe();

  tournamentSubscription = requestStream<TournamentsUpdateResponse>({
    service: GRAPHQL_API_URL.WS,
    body: { query: DOCUMENT_DATA_TYPE_MAP[dataKey].SUBSCRIPTION, variables },
    ...options
  }).subscribe(
    ({ data: { data = {} } = {} }) =>
      updateTournament({
        tournaments: data?.[subscriptionDataKey] || [],
        locale,
        timeZone: timeZoneName
      }),
    (err) => setSubscriptionError(err?.message)
  );
}

export async function filtersDataRequested<T extends WidgetReference>(
  _payload: undefined,
  { dispatch, select, widget, rootState, request }: WidgetEffectParams<T>
) {
  const { setFiltersData, setFiltersError }: WidgetDispatch = dispatch[widget.uid];
  const site = select.cms.getSiteName(rootState);
  const siteId = select.cms.getSiteId(rootState);
  const platform = select.device.getPlatform(rootState);
  const platformType = CLIENT_HSP_PLATFORM_MAP[platform];

  try {
    const { body } = await request({
      service: 'tags.resolve',
      body: { tag: ResolverTag.MTTGameFilters, brand: BrandTypes.POKER_STARS, site: siteId }
    });
    const filtersResponseData: FiltersConfig[] = await fetchJson(body?.url);
    const filtersData = normalizeFiltersData(filtersResponseData, site, platformType);
    setFiltersData(filtersData);
  } catch (err) {
    const errMessage = (err as Error)?.message;
    setFiltersError(errMessage);
  }
}

export const generateMTTEffects = <T extends WidgetReference>() => ({
  preload: preload<T>,
  tournamentsRequested: tournamentsRequested<T>,
  subscribeToTournaments: subscribeToTournaments<T>,
  filtersDataRequested: filtersDataRequested<T>
});
